import React, { useEffect, useRef, useState } from 'react';
import { Check, SlidersHorizontal } from 'lucide-react';
import { searchByOptions } from '../../data/mockGlobalSearch';
interface SearchByFilterProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  /** Options to choose from; defaults to the global search options */
  options?: {
    value: T;
    label: string;
  }[];
  /** The unfiltered option; the icon is highlighted when anything else is chosen */
  defaultValue?: T;
}

// Small icon button that sits inside a search input and opens the "Search by" menu
export function SearchByFilter<T extends string>({
  value,
  onChange,
  options,
  defaultValue
}: SearchByFilterProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const list = (options ?? searchByOptions) as {
    value: T;
    label: string;
  }[];
  const isFiltered = value !== (defaultValue ?? 'all');
  const currentLabel = list.find(option => option.value === value)?.label ?? 'All';
  return <div className="relative" ref={ref} data-id="element-3362" data-mp="2wfji5bmnpilc">
      <button type="button" onClick={() => setIsOpen(open => !open)} aria-haspopup="listbox" aria-expanded={isOpen} aria-label={`Search by: ${currentLabel}`} title={`Search by: ${currentLabel}`} className={`relative flex items-center justify-center w-8 h-8 rounded-md transition-colors ${isOpen || isFiltered ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]'}`} data-id="element-3363" data-mp="3agchc9nwl1us">
        <SlidersHorizontal className="w-4 h-4" data-id="element-3364" data-mp="3t6ls47xra1y3" />
        {isFiltered && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#0EA5E9]" aria-hidden="true" data-id="element-3365" data-mp="3t50zwoywyv3e" />}
      </button>

      {isOpen && <div role="listbox" aria-label="Search by" className="absolute right-0 top-full mt-2 w-56 bg-white border border-[#E2E8F0] rounded-lg shadow-xl z-[1000] overflow-hidden" data-id="element-3366" data-mp="3udwvdux5tz">
          <div className="px-3 pt-3 pb-1 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-3367" data-mp="a1ibdt2ukrfu">
            Search by
          </div>
          <div className="p-1" data-id="element-3368" data-mp="et9zlwyj6l2t">
            {list.map(option => {
          const selected = option.value === value;
          return <button key={option.value} type="button" role="option" aria-selected={selected} onClick={() => {
            onChange(option.value);
            setIsOpen(false);
          }} className={`w-full flex items-center justify-between text-left px-3 py-2.5 text-[14px] rounded-md transition-colors ${selected ? 'bg-[#E0F2FE] text-[#0F172A] font-medium' : 'text-[#0F172A] hover:bg-[#F1F5F9]'}`} data-id="element-3369" data-mp="itk8bhdpjpld">
                  {option.label}
                  {selected && <Check className="w-4 h-4 text-[#0EA5E9]" data-id="element-3370" data-mp="2zhxt9coi8402" />}
                </button>;
        })}
          </div>
        </div>}
    </div>;
}